/* eslint-disable object-curly-newline */
/* global React */
import { useMemo, useCallback } from 'react'
import { Grid, Button } from '@mui/material'
import { isObj } from 'lib/util'
import { useMemoRef, useStyles } from '@platform/react/hook'
import Icon from 'ui/Element/Icon'
import LabeledText from 'ui/Element/Text/Labeled'
import Text from 'ui/Element/Text'
import OfflineHOC from 'platform/react/hoc/offline'

/**
 * Sizes of the element
 *
 * https://www.figma.com/design/kh0cUkKrJQ8Zk1GNdqVT0N/Web-3.0---Library?node-id=73-991&m=dev
 *
 * @type {Object<string, {padding: number, iconPadding: ?string}>}
 */
const sizes = {
  medium: { padding: 1.5, iconPadding: '0.75rem' },
  small: { padding: 1, iconPadding: '0.5rem' },
  tiny: { padding: 1.5, iconPadding: null },
}

/**
 * Returns the ListItemButton element's current theme styles.
 *
 * @param {Object} theme          the application's current theme
 * @param {Object} props          the element's properties
 * @param {boolean} props.circle  whether the icon is displayed inside a circle
 * @returns {Object}              the ListItemButton component's styles
 */
const styles = (theme, { circle }) => ({
  root: {
    borderRadius: '0.5rem',
    // inherit parent transition so icon and button transition line up correctly
    '& *': { transition: 'inherit' },
    '&:hover': {
      backgroundColor: theme.palette.gray[950],
      ...circle && {
        '& > :first-of-type > *': {
          backgroundColor: theme.palette.gray[900],
        },
      },
    },
  },
  ...theme.custom.listItemButton,
})

/**
 * A ListItemButton element with some options to configure it.
 *
 * This is a row consisting of an icon on the left, a label and value on the right, optional text
 * further to the right and a chevron at the end.
 *
 * @param {Object} ref                    a reference to the root Button element
 * @param {Object} [events]               an object containing the element's event handlers
 * @param {Function} [events.onClick]     an event handler function called whenever
 *                                        the button is clicked
 * @param {String} value                  the button's text TODO: rename to label
 * @param {Boolean} [fullWidth=true]      whether the button should take parent's whole width
 * @param {Object} [props]                additional button's properties
 * @param {String} [props.icon]           the name of an icon to be displayed
 * @param {string} [props.size='medium']  the size of the row, `medium`, `small` or `tiny`
 *
 * @returns {JSX.Element}                 a new ListItem button element
 */
const ListItemButton = ({ ref, events, fullWidth = true, ...props }) => {
  const {
    icon = {},
    label,
    value,
    text,
    disabled,
    children,
    variant,
    color,
    textVariant,
    size = 'medium',
  } = props

  const { padding, iconPadding } = useMemo(() => sizes[size] || sizes.medium, [size])
  const classes = useStyles(styles, { circle: !!iconPadding })()
  const handleClick = useCallback(event => events?.onClick?.(event), [events])

  return (
    <Grid
      ref={ref}
      container
      component={Button}
      className={classes.root}

      disabled={disabled}
      fullWidth={fullWidth}
      onClick={handleClick}

      padding={padding}
      columnGap={1.5}
      flexWrap={'nowrap'}
      alignItems={'center'}
      justifyContent={'flex-start'}
      paddingLeft={{ xs: 1, sm: padding }}
    >
      <Grid
        item
        xs={'auto'}
      >
        <Icon
          name={'info'}
          source={icon}
          color={'gray.400'}
          padding={iconPadding}
          background={'gray.960'}
          shape={iconPadding ? 'circle' : null}
          // size={20}
        />
      </Grid>
      <Grid
        item
        container
        xs={true}
        zeroMinWidth={true}

        flexWrap={'nowrap'}
        alignItems={'center'}
        sx={{ textAlign: 'left' }}
        columnGap={{ xs: 0, sm: 2 }}
        justifyContent={'space-between'}
      >
        <Grid
          item
          zeroMinWidth={!text}
          xs={text ? 'auto' : true}
        >
          <LabeledText
            label={label}
            value={value}
            autoHide={false}
            itemsGap={0.25}
            children={children}

            color={color}
            background={'none'}
            {...variant && isObj(variant) && { variant }}

            alignItems={'center'}
            justifyContent={'flex-start'}
          />
        </Grid>
        {text && (
          <Grid
            item
            xs={6}

            value={text}
            component={Text}
            {...textVariant && { variant: textVariant }}
          />
        )}
        <Grid
          item
          xs={'auto'}
        >
          <Icon
            color={'gray.500'}
            name={'chevron-right'}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}

export default useMemoRef(OfflineHOC(ListItemButton), props => [
  props.value,
  props.disabled,
  props.events,
  props.children,
])
